/**
 * Resolve which NetSuite earning type an entry posts under.
 *
 * An entry carries an ENTRY KIND (worked time, PTO, holiday…) and a DEPARTMENT.
 * Those two select a set of candidate earning types; the employee's OWN assigned
 * earnings then pick exactly one. When the employee holds none of the candidates
 * the entry must NOT be posted under a guess — it is flagged, naming what payroll
 * has to add to that employee's file.
 *
 * Pure: the caller supplies the employee's earning ids, so this runs identically
 * in the API and (later) in the browser for pre-submit flagging.
 */
import { EARNING_MAP } from './earning-map.generated.js';
export { EARNING_MAP } from './earning-map.generated.js';
/**
 * Pick the earning type for (kind, department) given the employee's assigned
 * earnings. Never guesses: anything other than a single match is reported for the
 * caller to surface, because a wrong earning type is wrong pay.
 */
export function resolveEarning(kind, nsDepartmentId, employeeEarningIds) {
    const candidates = EARNING_MAP.filter((m) => m.kind === kind && m.nsDepartmentIds.includes(nsDepartmentId));
    if (candidates.length === 0) {
        return { status: 'unmapped', candidates: [] };
    }
    const held = candidates.filter((c) => employeeEarningIds.has(c.nsPayrollItemId));
    if (held.length === 1) {
        return {
            status: 'resolved',
            nsPayrollItemId: held[0].nsPayrollItemId,
            candidates: held.map((c) => c.name),
        };
    }
    if (held.length === 0) {
        return { status: 'missing', candidates: candidates.map((c) => c.name) };
    }
    return { status: 'ambiguous', candidates: held.map((c) => c.name) };
}
/**
 * A human-readable reason for a non-resolved outcome, suitable for a skip reason
 * or a Flag cell. States what is wrong AND what to do about it.
 */
export function earningResolutionReason(resolution, kind, departmentName) {
    const list = resolution.candidates.join(', ');
    switch (resolution.status) {
        case 'missing':
            return (`no ${kind} earning type on this employee's file for ${departmentName} — ` +
                `add one of: ${list}`);
        case 'ambiguous':
            return (`this employee has more than one ${kind} earning type for ${departmentName} ` +
                `(${list}) — remove the one that does not apply`);
        case 'unmapped':
            return `no ${kind} earning type is mapped to ${departmentName}`;
        /* c8 ignore next 2 */
        case 'resolved':
            return '';
        default:
            return '';
    }
}
/**
 * The payroll items that pay WORKED TIME (`kind: 'wage'`).
 *
 * WHY IT IS WORTH NAMING: an employee's NetSuite Earnings sublist holds items of
 * every kind at once — BONUS, HOLIDAY, ER HEALTH COST, expense reimbursement — and
 * only the wage ones say what their CLOCKED HOURS are paid under. A roster column
 * that lists the whole sublist is several lines of mostly-irrelevant text per
 * employee, and it buries the one value a time clock cares about.
 *
 * LAZY for the same reason as {@link import('./overtime').visaEarningItemIds}:
 * computing it at module load pins EARNING_MAP — the whole NetSuite earnings
 * catalogue — into every bundle that merely imports the shared barrel, including
 * the kiosk PWA. Behind a function it is tree-shakeable.
 */
let wageItemIds;
export function wageEarningItemIds() {
    wageItemIds ??= new Set(EARNING_MAP.filter((m) => m.kind === 'wage').map((m) => m.nsPayrollItemId));
    return wageItemIds;
}
/**
 * Decide whether an entry needs flagging, for ANY earning kind.
 *
 * Returns `undefined` when the earning resolves cleanly — absence IS the "all
 * clear", so a caller never has to interpret a status. This is the single place
 * that turns a resolution into a user-facing flag, so worked time, PTO and every
 * future report page describe the same problem the same way, in the same words the
 * TimeBill poster uses when it skips a group.
 *
 * @param nsDepartmentId the NetSuite department the pay would bill to — for a split
 *   employee that is the allocation share's department, NOT where they clocked.
 */
export function resolveEarningFlag(kind, nsDepartmentId, departmentName, heldEarningIds) {
    if (nsDepartmentId === null || nsDepartmentId === '') {
        return {
            kind,
            status: 'department-unmapped',
            departmentName,
            reason: `${departmentName} is not mapped to a NetSuite department, so no ${kind} ` +
                `earning type can be resolved for it`,
            candidates: [],
        };
    }
    const resolution = resolveEarning(kind, nsDepartmentId, heldEarningIds);
    if (resolution.status === 'resolved') {
        return undefined;
    }
    return {
        kind,
        status: resolution.status,
        departmentName,
        reason: earningResolutionReason(resolution, kind, departmentName),
        candidates: resolution.candidates,
    };
}
//# sourceMappingURL=earnings.js.map